const money = new Intl.NumberFormat('en-US', {
  style: 'currency',
  currency: 'USD',
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

export const QUANTITY_OFFER_TYPES = Object.freeze({
  UNIT_PRICE: 'unit_price',
  AFTER_FIRST_HALF_OFF: 'after_first_half_off',
  BOGO_HALF_OFF: 'bogo_half_off',
});

const HALF_OFF_TYPES = new Set([
  QUANTITY_OFFER_TYPES.AFTER_FIRST_HALF_OFF,
  QUANTITY_OFFER_TYPES.BOGO_HALF_OFF,
]);

function roundMoney(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100) / 100;
}

function moneyCents(value) {
  return Math.round((Number(value) + Number.EPSILON) * 100);
}

function centsMoney(value) {
  return Number(value) / 100;
}

function normalizedBaseQuantity(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? Number(parsed.toFixed(8)) : 0;
}

export function isValidQuantityOfferDiscountPercent(value) {
  const raw = String(value ?? '').trim();
  if (!/^(?:\d+(?:\.\d{0,2})?|\.\d{1,2})$/.test(raw)) return false;
  const percent = Number(raw);
  return Number.isFinite(percent) && percent > 0 && percent <= 100;
}

export function quantityOfferDiscountPercent(offer) {
  const raw = offer?.quantity_offer_discount_percent;
  if (raw == null || raw === '') return 50;
  const percent = Number(raw);
  if (!Number.isFinite(percent) || percent <= 0 || percent > 100 || Number(percent.toFixed(2)) !== percent) return 50;
  return percent;
}

function formatPercent(value) {
  return new Intl.NumberFormat('en-US', { maximumFractionDigits: 2 }).format(value);
}

export function quantityOfferType(offer) {
  return Object.values(QUANTITY_OFFER_TYPES).includes(offer?.quantity_offer_type)
    ? offer.quantity_offer_type
    : QUANTITY_OFFER_TYPES.UNIT_PRICE;
}

export function isHalfOffQuantityOffer(offer) {
  return HALF_OFF_TYPES.has(quantityOfferType(offer));
}

export function quantityOfferDiscountedBaseQuantity(offer, eligibleBaseQuantity) {
  const quantity = Math.max(0, normalizedBaseQuantity(eligibleBaseQuantity));
  switch (quantityOfferType(offer)) {
    case QUANTITY_OFFER_TYPES.AFTER_FIRST_HALF_OFF:
      return Number(Math.max(quantity - 1, 0).toFixed(8));
    case QUANTITY_OFFER_TYPES.BOGO_HALF_OFF:
      return Math.floor(quantity / 2);
    default:
      return 0;
  }
}

export function quantityOfferHalfOffTargetTotal(offer, eligibleBaseQuantity, regularUnitPrice = offer?.regular_unit_price) {
  if (!isHalfOffQuantityOffer(offer)) return null;
  const quantity = Math.max(0, normalizedBaseQuantity(eligibleBaseQuantity));
  const regular = Number(regularUnitPrice);
  if (!Number.isFinite(regular) || regular < 0) return null;
  const discountedQuantity = quantityOfferDiscountedBaseQuantity(offer, quantity);
  return roundMoney(regular * (quantity - discountedQuantity * quantityOfferDiscountPercent(offer) / 100));
}

export function quantityOfferSampleTotals(offer, regularUnitPrice, quantities = [1, 2, 3, 4, 5, 7]) {
  return quantities.map((quantity) => ({
    quantity,
    total: quantityOfferHalfOffTargetTotal(offer, quantity, regularUnitPrice),
  }));
}

export function formatQuantity(value) {
  const number = Number(value);
  if (!Number.isFinite(number)) return '';
  return number.toLocaleString('en-US', { maximumFractionDigits: 3 });
}

export function formatQuantityOffer(offer, unitLabel = '') {
  const type = quantityOfferType(offer);
  const unit = String(unitLabel || '').trim();
  const simpleUnit = !unit || /^(ea|each|unit|units)$/i.test(unit);
  const singularUnit = simpleUnit ? 'unit' : unit;
  if (type === QUANTITY_OFFER_TYPES.AFTER_FIRST_HALF_OFF) {
    return `Every ${singularUnit} after the first is ${formatPercent(quantityOfferDiscountPercent(offer))}% off`;
  }
  if (type === QUANTITY_OFFER_TYPES.BOGO_HALF_OFF) {
    return `Buy one ${singularUnit}, get the next ${formatPercent(quantityOfferDiscountPercent(offer))}% off`;
  }
  const minimum = Number(offer?.minimum_quantity);
  const price = Number(offer?.sale_price);
  if (!Number.isFinite(minimum) || minimum <= 0 || !Number.isFinite(price) || price < 0) return '';
  const suffix = simpleUnit
    ? 'each'
    : `per ${unit}`;
  return `Buy ${formatQuantity(minimum)} or more for ${money.format(price)} ${suffix}`;
}

export function hasLowerQuantityOfferPrice(offer, currentPrice, baseUnitsPerQuantity = 1) {
  const normalPrice = Number(currentPrice);
  const rawConversion = Number(baseUnitsPerQuantity);
  const conversion = Number.isFinite(rawConversion) && rawConversion > 0 ? rawConversion : 1;
  if (isHalfOffQuantityOffer(offer)) {
    const reference = Number(offer?.regular_unit_price);
    if (!Number.isFinite(reference) || reference <= 0 || !Number.isFinite(normalPrice) || normalPrice <= 0) return false;
    const ordinaryBaseRate = normalPrice / conversion;
    const discountFraction = quantityOfferDiscountPercent(offer) / 100;
    const bestLongRunRate = reference * (quantityOfferType(offer) === QUANTITY_OFFER_TYPES.BOGO_HALF_OFF
      ? 1 - discountFraction / 2
      : 1 - discountFraction);
    return bestLongRunRate < ordinaryBaseRate;
  }
  const offerPrice = Number(offer?.sale_price);
  return Number.isFinite(offerPrice)
    && offerPrice >= 0
    && Number.isFinite(normalPrice)
    && offerPrice * conversion < normalPrice;
}

export function quantityOfferBaseQuantity(items = [], productId) {
  const targetId = productId == null ? '' : String(productId);
  if (!targetId || !Array.isArray(items)) return 0;
  let total = 0;
  for (const line of items) {
    const product = line?.product || line || {};
    const lineProductId = String(line?.product_id ?? product.product_id ?? product.id ?? '');
    if (lineProductId !== targetId) continue;
    const offer = line?.quantity_offer || product.quantity_offer;
    const pricingSources = [line?.pricing_source, product.pricing_source];
    if (!offer
      || line?.quantity_offer_eligible === false
      || product.quantity_offer_eligible === false
      || line?.manual_price_override === true
      || product.manual_price_override === true
      || line?.uom_price_override != null
      || product.uom_price_override != null
      || product.price_override != null
      || pricingSources.some((source) => ['customer_product_price', 'manual_override', 'uom_override'].includes(source))) continue;
    const quantity = Number(line?.quantity ?? line?.qty ?? 0);
    const conversion = Number(
      line?.uom_conversion_factor_snapshot
      ?? line?.conversion_factor
      ?? line?.base_quantity_per_unit
      ?? product.base_quantity_per_unit
      ?? 1,
    );
    if (!Number.isFinite(quantity) || quantity <= 0) continue;
    const factor = Number.isFinite(conversion) && conversion > 0 ? conversion : 1;
    const converted = Number((quantity * factor).toFixed(8));
    total = Number((total + converted).toFixed(8));
  }
  return total;
}

export function quantityOfferQualifies(offer, eligibleBaseQuantity) {
  const rawMinimum = Number(offer?.minimum_quantity);
  const baseQuantity = Number(eligibleBaseQuantity);
  if (!Number.isFinite(rawMinimum) || rawMinimum <= 0 || !Number.isFinite(baseQuantity)) return false;
  const minimum = Number(rawMinimum.toFixed(3));
  return baseQuantity >= minimum;
}

export function getQuantityOfferUnitPrice(offer, currentPrice, eligibleBaseQuantity, baseUnitsPerQuantity = 1) {
  if (isHalfOffQuantityOffer(offer)) return currentPrice;
  const minimum = Number(offer?.minimum_quantity);
  const offerBasePrice = Number(offer?.sale_price);
  const current = Number(currentPrice);
  const conversion = Number(baseUnitsPerQuantity);
  const factor = Number.isFinite(conversion) && conversion > 0 ? conversion : 1;
  if (!Number.isFinite(minimum) || minimum <= 0
    || !Number.isFinite(offerBasePrice) || offerBasePrice < 0
    || !Number.isFinite(current)
    || !quantityOfferQualifies(offer, eligibleBaseQuantity)) return currentPrice;
  const unitOfferPrice = Math.round((offerBasePrice * factor + Number.EPSILON) * 100) / 100;
  return unitOfferPrice < current ? unitOfferPrice : currentPrice;
}

function lineProduct(line) {
  return line?.product || line || {};
}

function lineQuantity(line) {
  return Number(line?.quantity ?? line?.qty ?? 0);
}

function lineOrdinaryNetCents(line) {
  const explicitNet = line?.current_line_net ?? line?.ordinary_line_net;
  if (explicitNet != null && Number.isFinite(Number(explicitNet))) return Math.max(0, moneyCents(explicitNet));
  const explicitCents = line?.current_line_net_cents ?? line?.ordinary_line_net_cents;
  if (explicitCents != null && Number.isFinite(Number(explicitCents))) return Math.max(0, Math.round(Number(explicitCents)));
  const quantity = lineQuantity(line);
  const unitPrice = Number(line?.current_unit_price ?? line?.unit_price ?? line?.unitPrice);
  if (!Number.isFinite(quantity) || quantity <= 0 || !Number.isFinite(unitPrice)) return 0;
  const gross = line?.line_subtotal ?? line?.lineSubtotal;
  const lineDiscount = line?.line_discount ?? line?.lineDiscount ?? 0;
  if (gross != null && Number.isFinite(Number(gross))) {
    return Math.max(0, moneyCents(Number(gross) - Number(lineDiscount || 0)));
  }
  const discountPercent = Number(line?.discount_percent || 0);
  return Math.max(0, moneyCents(unitPrice * quantity * (1 - discountPercent / 100)));
}

function lineStableOrder(left, right) {
  const normalizedLeftSort = Number.parseInt(left.line?.sort_order || 0, 10);
  const normalizedRightSort = Number.parseInt(right.line?.sort_order || 0, 10);
  if (normalizedLeftSort !== normalizedRightSort) return normalizedLeftSort - normalizedRightSort;
  const leftId = String(left.line?.id ?? '');
  const rightId = String(right.line?.id ?? '');
  if (leftId !== rightId) return leftId.localeCompare(rightId);
  return left.index - right.index;
}

/**
 * Allocate half-off savings against ordinary line nets without inventing an
 * averaged unit price. Lines should represent ordinary gross rates and nets
 * before this offer; `current_line_net` is preferred when a caller already
 * knows the exact ordinary net amount for that line/segment.
 */
export function allocateQuantityOfferSavings(lines = [], offer, productId = null) {
  const source = Array.isArray(lines) ? lines : [];
  const offerLine = source.find((line) => line?.quantity_offer || lineProduct(line).quantity_offer);
  const product = lineProduct(offerLine);
  const targetId = productId == null
    ? String(offerLine?.product_id ?? product.product_id ?? product.id ?? '')
    : String(productId);
  const selectedOffer = offer || offerLine?.quantity_offer || product.quantity_offer;
  const eligible = source.map((line, index) => ({ line, index })).filter(({ line }) => {
    const product = lineProduct(line);
    const lineProductId = String(line?.product_id ?? product.product_id ?? product.id ?? '');
    const lineOffer = line?.quantity_offer || product.quantity_offer || selectedOffer;
    const pricingSources = [line?.pricing_source, product.pricing_source];
    return (!targetId || lineProductId === targetId)
      && isHalfOffQuantityOffer(lineOffer)
      && line?.quantity_offer_eligible !== false
      && product.quantity_offer_eligible !== false
      && line?.manual_price_override !== true
      && product.manual_price_override !== true
      && line?.uom_price_override == null
      && product.uom_price_override == null
      && product.price_override == null
      && !pricingSources.some((value) => ['customer_product_price', 'manual_override', 'uom_override'].includes(value));
  }).map((entry) => {
    const line = entry.line;
    const product = lineProduct(line);
    const quantity = lineQuantity(line);
    const conversionValue = Number(
      line?.uom_conversion_factor_snapshot
      ?? line?.conversion_factor
      ?? line?.base_quantity_per_unit
      ?? product.base_quantity_per_unit
      ?? 1,
    );
    const conversion = Number.isFinite(conversionValue) && conversionValue > 0 ? conversionValue : 1;
    return {
      ...entry,
      baseQuantity: quantity > 0 ? normalizedBaseQuantity(quantity * conversion) : 0,
      ordinaryNetCents: lineOrdinaryNetCents(line),
      grossSubtotalCents: (() => {
        const rawSubtotal = line?.quantity_offer_gross_subtotal ?? line?.line_subtotal ?? line?.lineSubtotal;
        if (rawSubtotal != null && Number.isFinite(Number(rawSubtotal))) return Math.max(0, moneyCents(rawSubtotal));
        const grossUnitPrice = Number(line?.quantity_offer_gross_unit_price ?? line?.unit_price ?? line?.unitPrice ?? 0);
        return Number.isFinite(grossUnitPrice) ? Math.max(0, moneyCents(grossUnitPrice * Math.max(0, quantity))) : 0;
      })(),
      minimumNetCents: (() => {
        const rawFloor = line?.quantity_offer_minimum_net ?? line?.min_price;
        return rawFloor != null && Number.isFinite(Number(rawFloor))
          ? Math.max(0, moneyCents(rawFloor))
          : 0;
      })(),
    };
  }).filter((entry) => entry.baseQuantity > 0);

  const eligibleBaseQuantity = eligible.reduce((sum, entry) => normalizedBaseQuantity(sum + entry.baseQuantity), 0);
  const ordinaryNetCents = eligible.reduce((sum, entry) => sum + entry.ordinaryNetCents, 0);
  const targetTotal = quantityOfferHalfOffTargetTotal(selectedOffer, eligibleBaseQuantity);
  const minimumGroupNetCents = eligible.reduce((sum, entry) => (
    sum + Math.min(entry.ordinaryNetCents, entry.minimumNetCents)
  ), 0);
  const targetTotalCents = targetTotal == null
    ? ordinaryNetCents
    : Math.max(moneyCents(targetTotal), minimumGroupNetCents);
  const discountedBaseQuantity = quantityOfferDiscountedBaseQuantity(selectedOffer, eligibleBaseQuantity);
  const savingsCents = discountedBaseQuantity > 0
    ? Math.max(0, ordinaryNetCents - targetTotalCents)
    : 0;
  const discounts = new Map(eligible.map(({ index }) => [index, 0]));

  if (savingsCents > 0 && ordinaryNetCents > 0) {
    const sharesByIndex = new Map(eligible.map((entry) => [entry.index, 0]));
    let undistributedCents = savingsCents;
    let active = eligible.filter((entry) => (
      entry.ordinaryNetCents > Math.min(entry.ordinaryNetCents, entry.minimumNetCents)
    ));
    while (undistributedCents > 0 && active.length > 0) {
      const totalWeight = active.reduce((sum, entry) => sum + entry.ordinaryNetCents, 0);
      if (totalWeight <= 0) break;
      const roundStart = undistributedCents;
      const shares = active.map((entry) => {
        const alreadyAllocated = sharesByIndex.get(entry.index) || 0;
        const capacity = Math.max(0, entry.ordinaryNetCents - Math.min(entry.ordinaryNetCents, entry.minimumNetCents));
        const rawShare = roundStart * entry.ordinaryNetCents / totalWeight;
        const cents = Math.min(capacity - alreadyAllocated, Math.floor(rawShare));
        return { ...entry, cents, remainder: rawShare - Math.floor(rawShare), capacity };
      });
      let assigned = 0;
      for (const share of shares) {
        if (share.cents <= 0) continue;
        sharesByIndex.set(share.index, (sharesByIndex.get(share.index) || 0) + share.cents);
        assigned += share.cents;
      }
      undistributedCents -= assigned;
      active = active.filter((entry) => {
        const capacity = Math.max(0, entry.ordinaryNetCents - Math.min(entry.ordinaryNetCents, entry.minimumNetCents));
        return (sharesByIndex.get(entry.index) || 0) < capacity;
      });
      if (undistributedCents <= 0 || active.length === 0) break;
      const remainderOrder = shares
        .filter((share) => (sharesByIndex.get(share.index) || 0) < share.capacity)
        .sort((left, right) => right.remainder - left.remainder || lineStableOrder(left, right));
      let remainderAssigned = 0;
      for (const share of remainderOrder) {
        if (undistributedCents <= 0) break;
        sharesByIndex.set(share.index, (sharesByIndex.get(share.index) || 0) + 1);
        undistributedCents -= 1;
        remainderAssigned += 1;
      }
      active = active.filter((entry) => {
        const capacity = Math.max(0, entry.ordinaryNetCents - Math.min(entry.ordinaryNetCents, entry.minimumNetCents));
        return (sharesByIndex.get(entry.index) || 0) < capacity;
      });
      if (assigned === 0 && remainderAssigned === 0) break;
    }
    for (const [index, cents] of sharesByIndex) discounts.set(index, cents);
  }

  const offerApplied = savingsCents > 0 && eligible.reduce((sum, entry) => sum + (discounts.get(entry.index) || 0), 0) > 0;
  const allocations = source.map((line, index) => {
    const applicable = discounts.has(index);
    const entry = eligible.find((candidate) => candidate.index === index);
    const incrementalSavingsCents = applicable ? discounts.get(index) || 0 : 0;
    const currentNetCents = applicable ? entry.ordinaryNetCents : 0;
    const finalNetCents = applicable ? Math.max(0, currentNetCents - incrementalSavingsCents) : 0;
    const ordinaryDiscountCents = applicable
      ? Math.max(0, entry.grossSubtotalCents - currentNetCents)
      : 0;
    const fullGrossToNetDiscountCents = applicable
      ? Math.max(0, entry.grossSubtotalCents - finalNetCents)
      : 0;
    return {
      line,
      quantity_offer_applied: offerApplied && applicable,
      quantity_offer_type_snapshot: offerApplied && applicable ? quantityOfferType(selectedOffer) : null,
      line_discount: centsMoney(fullGrossToNetDiscountCents),
      promotion_discount_amount: offerApplied ? centsMoney(fullGrossToNetDiscountCents) : 0,
      incremental_offer_savings: centsMoney(incrementalSavingsCents),
      ordinary_line_discount: centsMoney(ordinaryDiscountCents),
      final_line_net: applicable ? centsMoney(finalNetCents) : null,
      ordinary_line_net: applicable ? centsMoney(currentNetCents) : null,
      base_quantity: entry?.baseQuantity ?? 0,
    };
  });

  return {
    quantity_offer_applied: offerApplied,
    quantity_offer_type: quantityOfferType(selectedOffer),
    eligible_base_quantity: eligibleBaseQuantity,
    discounted_base_quantity: discountedBaseQuantity,
    ordinary_total: centsMoney(ordinaryNetCents),
    target_total: targetTotal == null ? null : centsMoney(targetTotalCents),
    savings: centsMoney(offerApplied
      ? eligible.reduce((sum, entry) => sum + (discounts.get(entry.index) || 0), 0)
      : 0),
    allocations,
  };
}
